import React from 'react'
import { Badge, Image } from 'react-bootstrap'
import type { Asset } from '@ztechapps/common.contract'
import { AssetTypeEnum } from '@ztechapps/common.contract'
import { ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { useAssetPageContext } from './Context'
import { AssetActionButtons, AssetFieldInput } from './shared'
import type { RowProps } from '../Table/Row'

const getTypeBadgeVariant = (type: string) => {
  switch (type) {
    case AssetTypeEnum.image:
      return 'success'
    case AssetTypeEnum.video:
      return 'info'
    case AssetTypeEnum.document:
      return 'warning'
    case AssetTypeEnum.raw:
      return 'secondary'
    default:
      return 'secondary'
  }
}

export const ListViewRow = ({ _config, ...item }: RowProps<Asset>) => {
  const context = useAssetPageContext()
  const pdfIcon = context.pdfIcon
  const fileIcon = context.fileIcon
  const { dragProps, dragRowClassName } = _config ?? {}

  const originalUrl = item?.url
  const mediumUrl = item.mediumUrl || originalUrl
  const thumbUrl = item?.thumbUrl || mediumUrl

  const renderThumbnail = () => {
    const thumbnailStyle = { width: 40, height: 40, objectFit: 'cover' as const }

    if (item?.type === AssetTypeEnum.image) {
      return (
        <TooltipPopOver
          tooltipBody={<Image src={mediumUrl} style={{ maxWidth: 600 }} alt={item.title || 'Asset preview'} />}
        >
          <Image src={thumbUrl} style={thumbnailStyle} alt={item.title || 'Asset thumbnail'} />
        </TooltipPopOver>
      )
    }

    if (item?.type === AssetTypeEnum.document) {
      return <Image src={pdfIcon} style={thumbnailStyle} alt="PDF document" />
    }

    if (item?.type === AssetTypeEnum.video) {
      return (
        <div
          style={{
            ...thumbnailStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#e9ecef',
          }}
          role="img"
          aria-label="Video thumbnail"
        >
          <span style={{ fontSize: 20 }} aria-hidden="true">
            &#9658;
          </span>
        </div>
      )
    }

    return <Image src={fileIcon} style={thumbnailStyle} alt="File" />
  }

  return (
    <tr {...dragProps} className={dragRowClassName ?? ''}>
      <td className="align-middle text-center">{renderThumbnail()}</td>
      <td className="align-middle">
        <AssetFieldInput item={item as Asset} field="title" />
      </td>
      <td className="align-middle">
        <AssetFieldInput item={item as Asset} field="subtitle" />
      </td>
      <ShowIf condition={!!context.renderAssetStatus}>
        <td className="align-middle">{context.renderAssetStatus?.(item as Asset)}</td>
      </ShowIf>
      <td className="align-middle text-center">
        <Badge bg={getTypeBadgeVariant(item.type)}>{item.type}</Badge>
      </td>
      <td className="align-middle">
        <AssetActionButtons asset={item as Asset} />
      </td>
    </tr>
  )
}
